
import React, { useState, useEffect } from 'react';
import { Shield, Activity, Users, FileText, RefreshCw, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { motion } from 'framer-motion';

export default function AdminPanel({ onExit }) {
    const [activeTab, setActiveTab] = useState('overview');
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        activeSensors: 3,
        alertsToday: 12,
        activeUsers: 8,
        systemHealth: '98%'
    });

    useEffect(() => {
        if (activeTab === 'logs') fetchLogs();
    }, [activeTab]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await api.audit.getLogs();
            if (res.data) setLogs(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="app-content"
            style={{ padding: '1rem', height: '100%', overflowY: 'auto' }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#f59e0b', margin: 0 }}>
                    <Shield size={28} /> Admin Console
                </h2>
                <button onClick={onExit} className="btn" style={{ padding: '5px 15px', background: '#334155' }}>
                    Salir
                </button>
            </div>

            <div className="admin-tabs" style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                {['overview', 'logs', 'users'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '8px 16px',
                            background: activeTab === tab ? '#f59e0b' : 'rgba(255,255,255,0.05)',
                            border: 'none', borderRadius: '8px', color: 'white',
                            cursor: 'pointer', textTransform: 'capitalize'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {activeTab === 'overview' && (
                <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                    <StatCard title="Sensores Activos" value={stats.activeSensors} icon={Activity} color="#10b981" />
                    <StatCard title="Alertas Hoy" value={stats.alertsToday} icon={AlertTriangle} color="#ef4444" />
                    <StatCard title="Usuarios" value={stats.activeUsers} icon={Users} color="#3b82f6" />
                    <StatCard title="Salud Sistema" value={stats.systemHealth} icon={RefreshCw} color="#8b5cf6" />
                </div>
            )}

            {activeTab === 'logs' && (
                <div className="logs-container glass-effect" style={{ padding: 15 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15 }}>
                        <h3>Bitácora de Seguridad (Audit Logs)</h3>
                        <button onClick={fetchLogs}><RefreshCw size={16} /></button>
                    </div>

                    {loading ? <p>Cargando logs...</p> : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '400px', overflowY: 'auto' }}>
                            {logs.length === 0 ? <p style={{ color: '#94a3b8' }}>No hay registros recientes.</p> :
                                logs.map((log, idx) => (
                                    <div key={idx} style={{
                                        padding: '10px', background: 'rgba(255,255,255,0.03)',
                                        borderLeft: `3px solid ${getColorForAction(log.action)}`,
                                        fontSize: '0.85rem'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                            <span style={{ fontWeight: 'bold' }}>{log.action.toUpperCase()}</span>
                                            <span style={{ color: '#94a3b8' }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                                        </div>
                                        <div style={{ color: '#cbd5e1' }}>User: {log.userId} ({log.role})</div>
                                        <div style={{ color: '#64748b', fontSize: '0.75rem' }}>IP: {log.ip || 'Unknown'}</div>
                                        {log.details && <pre style={{ marginTop: 5, fontSize: '0.7rem', background: '#0f172a', padding: 5, borderRadius: 4 }}>
                                            {JSON.stringify(log.details, null, 2)}
                                        </pre>}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'users' && (
                <div className="glass-effect" style={{ padding: 20, textAlign: 'center' }}>
                    <Users size={48} color="#94a3b8" style={{ marginBottom: 15 }} />
                    <p>Gestión de Usuarios próximamente disponible.</p>
                </div>
            )}
        </motion.div>
    );
}

function StatCard({ title, value, icon: Icon, color }) {
    return (
        <div className="glass-effect" style={{ padding: 15, display: 'flex', alignItems: 'center', gap: 15 }}>
            <div style={{ padding: 10, borderRadius: 10, background: `${color}20`, color: color }}>
                <Icon size={24} />
            </div>
            <div>
                <div style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{title}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{value}</div>
            </div>
        </div>
    );
}

function getColorForAction(action) {
    if (action.includes('error') || action.includes('alert')) return '#ef4444';
    if (action.includes('login')) return '#10b981';
    return '#3b82f6';
}
